import React from 'react';
import './JobsAtRiskTable.css';

/**
 * JobsAtRiskTable Component
 * Displays a table of jobs needing attention with risk indicators
 */
function JobsAtRiskTable({ data, loading, onJobClick }) {
    if (loading) {
        return (
            <div className="jobs-table-container jobs-table-loading">
                <div className="chart-loading-spinner"></div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="jobs-table-container jobs-table-empty">
                <div className="empty-state">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#27AE60" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <p>All jobs are on track!</p>
                    <span>No jobs currently need attention</span>
                </div>
            </div>
        );
    }

    const getPriorityBadge = (priority) => {
        const priorityClasses = {
            'Hot': 'priority-hot',
            'High': 'priority-high',
            'Medium': 'priority-medium',
            'Low': 'priority-low'
        };
        return (
            <span className={`priority-badge ${priorityClasses[priority] || 'priority-low'}`}>
                {priority || 'N/A'}
            </span>
        );
    };

    const getRiskBadge = (riskLevel) => {
        const riskClasses = {
            'critical': 'risk-critical',
            'high': 'risk-high',
            'medium': 'risk-medium',
            'low': 'risk-low'
        };
        return (
            <span className={`risk-badge ${riskClasses[riskLevel] || 'risk-low'}`}>
                {riskLevel?.toUpperCase() || 'LOW'}
            </span>
        );
    };

    const getDaysOpenClass = (days) => {
        if (days > 60) return 'days-critical';
        if (days > 45) return 'days-warning';
        return 'days-normal';
    };

    return (
        <div className="jobs-table-container">
            <div className="jobs-table-header">
                <h3 className="chart-title">Jobs Needing Attention</h3>
                <span className="jobs-count">{data.length} job{data.length !== 1 ? 's' : ''}</span>
            </div>

            <div className="jobs-table-wrapper">
                <table className="jobs-table">
                    <thead>
                        <tr>
                            <th>Job Title</th>
                            <th>Location</th>
                            <th>Hiring Manager</th>
                            <th>Recruiter</th>
                            <th>Priority</th>
                            <th>Days Open</th>
                            <th>Active Candidates</th>
                            <th>Risk</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((job) => (
                            <tr
                                key={job.id}
                                className="job-row"
                                onClick={() => onJobClick && onJobClick(job)}
                            >
                                <td className="job-title-cell">
                                    <span className="job-title">{job.jobTitle}</span>
                                    {job.department && (
                                        <span className="job-department">{job.department}</span>
                                    )}
                                </td>
                                <td>{job.location || 'N/A'}</td>
                                <td>{job.hiringManager || 'N/A'}</td>
                                <td>{job.recruiter || 'N/A'}</td>
                                <td>{getPriorityBadge(job.priority)}</td>
                                <td className={getDaysOpenClass(job.daysOpen)}>
                                    {job.daysOpen} days
                                </td>
                                <td className={job.activeCandidates < 3 ? 'candidates-low' : ''}>
                                    {job.activeCandidates}
                                </td>
                                <td>{getRiskBadge(job.riskLevel)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default JobsAtRiskTable;
